import { TFunction, useAnalytics } from '@psyomics/ui-common';
import { useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Model } from 'survey-react-ui';

import {
  useDeleteSurveyAttachment,
  useGetSurveyAttachmentDownloadUrl,
} from '../../survey-responses/hooks/use-survey-attachments';
import { useUpdateSurveyResponse } from '../../survey-responses/hooks/use-update-survey-response';

import { createSurveyAttachmentHandlers } from './survey-attachment-handlers';
import { createSurveyEventHandlers } from './survey-event-handlers';
import { useDebouncedSurveyUpdate } from './use-debounced-survey-update';
import { useSurveyUpdateQueue } from './use-survey-update-queue';

type SurveyData = Record<string, unknown>;

type SurveyHandlers = ReturnType<typeof createSurveyEventHandlers>;
type SurveyAttachmentHandlers = ReturnType<typeof createSurveyAttachmentHandlers>;

function attachSurveyListeners(
  surveyModel: Model,
  handlers: SurveyHandlers,
  attachmentHandlers: SurveyAttachmentHandlers
): void {
  surveyModel.onCurrentPageChanged.add(handlers.onPageChange);
  surveyModel.onComplete.add(handlers.onComplete);
  surveyModel.onValueChanged.add(handlers.onValueChange);
  surveyModel.onUploadFiles.add(attachmentHandlers.onUploadFiles);
  surveyModel.onClearFiles.add(attachmentHandlers.onClearFiles);
  surveyModel.onDownloadFile.add(attachmentHandlers.onDownloadFile);
}

function detachSurveyListeners(surveyModel: Model, handlers: SurveyHandlers): void {
  surveyModel.onValueChanged.clear();
  surveyModel.onCurrentPageChanged.remove(handlers.onPageChange);
  surveyModel.onComplete.clear();
  surveyModel.onUploadFiles.clear();
  surveyModel.onClearFiles.clear();
  surveyModel.onDownloadFile.clear();
}

type CreateHandlersProps = {
  analytics: ReturnType<typeof useAnalytics>;
  deleteSurveyAttachment: ReturnType<typeof useDeleteSurveyAttachment>[1];
  getSurveyAttachmentDownloadUrl: ReturnType<typeof useGetSurveyAttachmentDownloadUrl>;
  invitePublicId: string;
  organisationCode?: string | null;
  patientApiUrl: string;
  serviceCode?: string | null;
  surveyResponseId: string;
  t: TFunction;
  updateSurvey: ReturnType<typeof useDebouncedSurveyUpdate>;
};

function createHandlers({
  analytics,
  deleteSurveyAttachment,
  getSurveyAttachmentDownloadUrl,
  invitePublicId,
  organisationCode,
  patientApiUrl,
  serviceCode,
  surveyResponseId,
  t,
  updateSurvey,
}: CreateHandlersProps): { handlers: SurveyHandlers; attachmentHandlers: SurveyAttachmentHandlers } {
  return {
    attachmentHandlers: createSurveyAttachmentHandlers({
      deleteSurveyAttachment,
      getSurveyAttachmentDownloadUrl,
      patientApiUrl,
      surveyResponseId,
      t,
    }),
    handlers: createSurveyEventHandlers({
      analytics,
      invitePublicId,
      organisationCode,
      serviceCode,
      updateSurvey,
    }),
  };
}

type UseSurveyHandlerProps = {
  invitePublicId: string;
  organisationCode?: string | null;
  patientApiUrl: string;
  ready: boolean;
  reviewMode: boolean;
  serviceCode?: string | null;
  setSurveyResults: (data: SurveyData) => void;
  surveyModel: Model;
  surveyResponseId: string;
  surveyResponseUpdatedAt?: Date;
  updateSurveyAnswers: ReturnType<typeof useUpdateSurveyResponse>[1];
};

function useAttachSurveyListeners(surveyModel: Model, handlerProps: CreateHandlersProps): void {
  const {
    analytics,
    deleteSurveyAttachment,
    getSurveyAttachmentDownloadUrl,
    invitePublicId,
    organisationCode,
    patientApiUrl,
    serviceCode,
    surveyResponseId,
    t,
    updateSurvey,
  } = handlerProps;

  useLayoutEffect(() => {
    const { handlers, attachmentHandlers } = createHandlers({
      analytics,
      deleteSurveyAttachment,
      getSurveyAttachmentDownloadUrl,
      invitePublicId,
      organisationCode,
      patientApiUrl,
      serviceCode,
      surveyResponseId,
      t,
      updateSurvey,
    });
    attachSurveyListeners(surveyModel, handlers, attachmentHandlers);
    return () => detachSurveyListeners(surveyModel, handlers);
  }, [
    analytics,
    deleteSurveyAttachment,
    getSurveyAttachmentDownloadUrl,
    invitePublicId,
    organisationCode,
    patientApiUrl,
    serviceCode,
    surveyModel,
    surveyResponseId,
    t,
    updateSurvey,
  ]);
}

const useSurveyHandlers = ({
  invitePublicId,
  organisationCode,
  patientApiUrl,
  ready,
  reviewMode,
  serviceCode,
  setSurveyResults,
  surveyModel,
  surveyResponseId,
  surveyResponseUpdatedAt,
  updateSurveyAnswers,
}: UseSurveyHandlerProps) => {
  const analytics = useAnalytics();
  const { t } = useTranslation();
  const [, deleteSurveyAttachment] = useDeleteSurveyAttachment();
  const getSurveyAttachmentDownloadUrl = useGetSurveyAttachmentDownloadUrl();

  const enqueueUpdateSurvey = useSurveyUpdateQueue({
    ready,
    reviewMode,
    setSurveyResults,
    surveyResponseId,
    surveyResponseUpdatedAt,
    updateSurveyAnswers,
  });
  const updateSurvey = useDebouncedSurveyUpdate(enqueueUpdateSurvey);

  useAttachSurveyListeners(surveyModel, {
    analytics,
    deleteSurveyAttachment,
    getSurveyAttachmentDownloadUrl,
    invitePublicId,
    organisationCode,
    patientApiUrl,
    serviceCode,
    surveyResponseId,
    t,
    updateSurvey,
  });
};

export { useSurveyHandlers };
